import { useState, useRef, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { toDecimal } from '../utils/exifGps';

// ── helpers ──────────────────────────────────────────────────────────────────

function toDMS(decimal, isLat) {
  const abs = Math.abs(decimal);
  const deg = Math.floor(abs);
  const minFull = (abs - deg) * 60;
  const min = Math.floor(minFull);
  const sec = ((minFull - min) * 60).toFixed(2);
  const dir = isLat ? (decimal >= 0 ? 'N' : 'S') : (decimal >= 0 ? 'E' : 'W');
  return `${deg}° ${min}' ${sec}" ${dir}`;
}

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtExposure(v) {
  if (!v) return null;
  if (v < 1) return `1/${Math.round(1 / v)}s`;
  return `${v}s`;
}

function fmtFocalLength(v) {
  if (!v) return null;
  return `${v} mm`;
}

async function extractExif(file) {
  const exifr = await import('exifr');
  const all = await exifr.parse(file, { tiff: true, xmp: true, iptc: false, gps: true, translateValues: true });
  const thumb = await exifr.thumbnail(file).catch(() => null);
  return { all: all || {}, thumb };
}

function buildResult(file, exifData) {
  const { all, thumb } = exifData;

  // GPS
  let gps = null;
  if (all.latitude != null && all.longitude != null) {
    gps = { lat: all.latitude, lng: all.longitude };
  } else if (all.GPSLatitude && all.GPSLongitude) {
    const lat = toDecimal(all.GPSLatitude, all.GPSLatitudeRef);
    const lng = toDecimal(all.GPSLongitude, all.GPSLongitudeRef);
    if (lat != null && lng != null) gps = { lat, lng };
  }

  const datetime =
    all.DateTimeOriginal ||
    all.CreateDate ||
    all.DateTime ||
    all.GPSDateStamp
      ? all.DateTimeOriginal || all.CreateDate || all.DateTime
      : null;

  return {
    id: Math.random().toString(36).slice(2),
    name: file.name,
    size: file.size,
    type: file.type || 'unknown',
    datetime,
    gps,
    make: all.Make || null,
    model: all.Model || null,
    software: all.Software || null,
    fNumber: all.FNumber || null,
    iso: all.ISO || null,
    exposureTime: all.ExposureTime || null,
    focalLength: all.FocalLength || null,
    imageWidth: all.ImageWidth || all.ExifImageWidth || all.PixelXDimension || null,
    imageHeight: all.ImageHeight || all.ExifImageHeight || all.PixelYDimension || null,
    flash: all.Flash != null ? String(all.Flash) : null,
    orientation: all.Orientation || null,
    raw: all,
    thumb,
    error: null,
  };
}

// ── sub-components ────────────────────────────────────────────────────────────

function KVRow({ label, value, mono = false }) {
  if (value == null || value === '') return null;
  return (
    <tr>
      <td style={{ padding: '6px 12px 6px 0', color: '#64748b', fontSize: 13, whiteSpace: 'nowrap', verticalAlign: 'top', fontWeight: 500 }}>
        {label}
      </td>
      <td style={{ padding: '6px 0', fontSize: 13, color: '#0f172a', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-word' }}>
        {value}
      </td>
    </tr>
  );
}

function Badge({ text, color, bg }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '.06em',
      textTransform: 'uppercase', padding: '2px 8px', borderRadius: 20,
      background: bg, color, border: `1px solid ${color}44`,
    }}>
      {text}
    </span>
  );
}

function RawPanel({ raw }) {
  const [open, setOpen] = useState(false);
  const entries = Object.entries(raw).filter(([, v]) => v != null && typeof v !== 'object');
  const objEntries = Object.entries(raw).filter(([, v]) => v != null && typeof v === 'object');

  const renderVal = (v) => {
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
          padding: '4px 12px', cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600,
        }}
      >
        {open ? '▲ Hide raw tags' : '▼ Show all raw tags'} ({Object.keys(raw).length})
      </button>
      {open && (
        <div style={{
          marginTop: 8, maxHeight: 300, overflowY: 'auto',
          border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <tbody>
              {[...entries, ...objEntries].map(([k, v]) => (
                <tr key={k} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '4px 10px', color: '#64748b', whiteSpace: 'nowrap', fontWeight: 500, width: 200 }}>{k}</td>
                  <td style={{ padding: '4px 10px', color: '#0f172a', fontFamily: 'monospace', wordBreak: 'break-all' }}>{renderVal(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResultCard({ result }) {
  if (result.error) {
    return (
      <div style={{
        background: '#fff', border: '1.5px solid #fca5a5', borderRadius: 12,
        padding: '20px 24px', color: '#dc2626',
      }}>
        <strong>{result.name}</strong>
        <p style={{ margin: '8px 0 0', fontSize: 13 }}>{result.error}</p>
      </div>
    );
  }

  const thumbUrl = result.thumb
    ? URL.createObjectURL(new Blob([result.thumb], { type: 'image/jpeg' }))
    : null;

  const mapsUrl = result.gps
    ? `https://www.google.com/maps?q=${result.gps.lat.toFixed(6)},${result.gps.lng.toFixed(6)}`
    : null;

  const dims = result.imageWidth && result.imageHeight
    ? `${result.imageWidth} × ${result.imageHeight} px`
    : null;

  return (
    <div style={{
      background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
      overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      {/* Card header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 20px', borderBottom: '1px solid #f1f5f9',
        background: '#f8fafc',
      }}>
        {thumbUrl && (
          <img src={thumbUrl} alt="thumb" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0', flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {result.name}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
            {fmtBytes(result.size)} · {result.type}
            {dims && ` · ${dims}`}
          </div>
        </div>
        {result.gps && <Badge text="GPS" color="#15803d" bg="#dcfce7" />}
      </div>

      {/* Card body */}
      <div style={{ padding: '16px 20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {/* GPS */}
            {result.gps && (
              <>
                <KVRow label="GPS Coordinates"
                  value={
                    <span>
                      {result.gps.lat.toFixed(6)}, {result.gps.lng.toFixed(6)}
                      {' '}
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#4f46e5', fontWeight: 600, fontSize: 12 }}
                      >
                        Open in Google Maps ↗
                      </a>
                    </span>
                  }
                />
                <KVRow label="Latitude (DMS)" value={toDMS(result.gps.lat, true)} mono />
                <KVRow label="Longitude (DMS)" value={toDMS(result.gps.lng, false)} mono />
              </>
            )}

            {/* Date/Time */}
            <KVRow
              label="Date / Time"
              value={result.datetime
                ? (result.datetime instanceof Date
                  ? result.datetime.toLocaleString()
                  : String(result.datetime))
                : null}
            />

            {/* Device */}
            <KVRow label="Make" value={result.make} />
            <KVRow label="Model" value={result.model} />
            <KVRow label="Software" value={result.software} />

            {/* Camera settings */}
            <KVRow label="Aperture" value={result.fNumber ? `f/${result.fNumber}` : null} />
            <KVRow label="ISO" value={result.iso} />
            <KVRow label="Shutter Speed" value={fmtExposure(result.exposureTime)} />
            <KVRow label="Focal Length" value={fmtFocalLength(result.focalLength)} />
            <KVRow label="Flash" value={result.flash} />
            <KVRow label="Orientation" value={result.orientation} />
          </tbody>
        </table>

        <RawPanel raw={result.raw} />
      </div>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function ExifViewer() {
  const [results, setResults] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const processFiles = useCallback(async (files) => {
    trackToolAction('/exif', 'EXIF Viewer');
    if (!files || files.length === 0) return;
    setProcessing(true);

    const newResults = [];
    for (const file of Array.from(files)) {
      try {
        const exifData = await extractExif(file);
        newResults.push(buildResult(file, exifData));
      } catch (err) {
        newResults.push({
          id: Math.random().toString(36).slice(2),
          name: file.name,
          size: file.size,
          type: file.type,
          error: err.message || 'Failed to read EXIF data',
          raw: {},
        });
      }
    }

    setResults(prev => [...prev, ...newResults]);
    setProcessing(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const clearAll = () => setResults([]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: '#0f172a',
    }}>
      <Navbar pageTitle="EXIF Viewer" />

      {/* Body */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 72px' }}>

        {/* Controls row */}
        {results.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>{results.length} file{results.length !== 1 ? 's' : ''}</span>
            <button
              onClick={clearAll}
              style={{
                background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
                padding: '5px 14px', cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 600,
              }}
            >
              Clear All
            </button>
          </div>
        )}

        {/* Drop zone */}
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? '#6366f1' : '#cbd5e1'}`,
            borderRadius: 12,
            padding: '40px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? '#eef2ff' : '#fff',
            transition: 'border-color .15s, background .15s',
            marginBottom: 32,
          }}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,.heic,.heif,.tiff,.tif"
            style={{ display: 'none' }}
            onChange={e => processFiles(e.target.files)}
          />
          <div style={{ fontSize: 36, marginBottom: 8 }}>🖼️</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 4 }}>
            {processing ? 'Processing…' : 'Drop images here or click to browse'}
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>
            Supports JPEG, PNG, TIFF, HEIC, WebP · Multiple files at once · All processing is local
          </div>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {results.map(r => <ResultCard key={r.id} result={r} />)}
          </div>
        )}

        {results.length === 0 && !processing && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, marginTop: 40 }}>
            Drop one or more photos to extract their EXIF metadata.
          </div>
        )}
      </div>
    </div>
  );
}
